import { isEmpty } from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  GeneralBannerType,
  T_CenterBannerData,
  T_FooterBannerData,
  T_MyTrackData,
  T_NewOfferingData,
  T_NudgeBannerData,
  T_TopBannerData,
} from "../features/Features_Cms/CmsGlobal.Types";
import { useLazySaveOfferMartAndPromotionalDetailQuery } from "../features/Feature_GeneralAppElements/GeneralAppElements_Api";
import {
  useAppDispatch,
  useAppSelector,
} from "../redux-store/CustomReduxHooks";
import {
  DEVICE_TYPE,
  EVENT_HANDLER,
  exclusiveAbcOffersQrCodePopupTitle,
  exploreAbcOfferingsQrCodePopupTitle,
  footerBannerName,
  myTrackName,
  newOfferingName,
  trackWhatMattersQrCodePopupTitle,
} from "../utils/constants";
import {
  getFormattedDateTime,
  getSuperAppRedirectionData,
} from "../utils/helper";
import { nativeCallBackMethodForAppPermission } from "../utils/NativeAppEvents";
import useLeadUtilities from "./useLeadUtilities";
import {
  getBannerCategory,
  getLeadCreationFlags,
  hasBannerMetrics,
  usePlatformFlags,
} from "./utils/bannerFilterUtils";
import useNativePermissionSync from "./useNativePermissionSync";
import useDeviceDetection from "./useDeviceDetection";

type PendingRedirection = {
  data: GeneralBannerType;
  buyJourneyUserName?: string;
  isAppLead: boolean;
  appsFlyerLink: string;
} | null;

const useLeadRedirectionHandler = (
  bannerName: string,
  qrListItems: string = "",
) => {
  const [isLeadPopupsOpen, setIsLeadPopupsOpen] = useState<boolean>(false);
  const [selectedCard, setSelectedCard] = useState<GeneralBannerType>();
  // Buy Journey Popup
  const [openBuyJourneyPopup, setOpenBuyJourneyPopup] = useState(false);

  // QR Code Popup
  const [openQrPopup, setOpenQrPopup] = useState(false);

  // Lead Intent Popup
  const [openLeadIntentPopup, setOpenLeadIntentPopup] = useState(false);

  // Lead Intent Confirmation Popup
  const [openLeadIntentConfirmationPopup, setOpenLeadIntentConfirmationPopup] =
    useState(false);

  // Loading state for Super App redirection
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const pendingRedirectionRef = useRef<PendingRedirection>(null);
  const { syncNativePermissions } = useNativePermissionSync();
  const dispatch = useAppDispatch();
  const [saveOfferMartAndPromotionalDetail] =
    useLazySaveOfferMartAndPromotionalDetailQuery();

  const csAppSlice = useAppSelector((state) => state.CsAppSlice);
  const userDetails = csAppSlice.userDetails;
  const tempLeadNameFromSlice = useAppSelector(
    (state) => state.CsAppSlice.tempLeadName,
  );
  const isPseudoUserLoggedIn = useAppSelector(
    (state) => state.PseudoLoginSlice.isPseudoLoggedIn,
  );

  const isTrackWhatMatter = useMemo(
    () => bannerName === myTrackName,
    [bannerName],
  );
  const isBuyNewProduct = useMemo(
    () => bannerName === newOfferingName,
    [bannerName],
  );

  const platformFlags = usePlatformFlags(
    csAppSlice.channel,
    csAppSlice.NativeDeviceInfo?.deviceName,
  );
  const deviceType = useDeviceDetection();
  const isMobileBrowser = deviceType === DEVICE_TYPE.ABC_WEB_MOB_CS; //true when runs on mobile browser

  const {
    generateLmsLeadIdForAppsFlyerRedirection,
    generateLeadIdAndTokenForLeadRedirection,
    openAppDestination,
    openWebDestination,
    proceedAppRedirect,
    sendMessageToNative,
    lmsLeadIdWithCallBackTimeC,
  } = useLeadUtilities(bannerName);

  // Memoize popup check logic
  const checkForNamePopup = useMemo(
    () =>
      !userDetails?.IS_USER_REGISTERED &&
      isEmpty(userDetails?.tempLeadName) &&
      isEmpty(tempLeadNameFromSlice),
    [
      userDetails?.IS_USER_REGISTERED,
      userDetails?.tempLeadName,
      tempLeadNameFromSlice,
    ],
  );

  const openBuyJourneyPopupWithCard = useCallback(() => {
    setOpenBuyJourneyPopup(true);
  }, []);

  const handleQrPopupOpen = useCallback(() => setOpenQrPopup(true), []);
  const handleQrPopupClose = useCallback(() => setOpenQrPopup(false), []);

  const qrTitle = useMemo(() => {
    if (bannerName === footerBannerName) {
      return exclusiveAbcOffersQrCodePopupTitle;
    } else if (bannerName === myTrackName) {
      return trackWhatMattersQrCodePopupTitle;
    } else if (bannerName === newOfferingName) {
      return exploreAbcOfferingsQrCodePopupTitle;
    } else {
      return selectedCard?.attributes.productName;
    }
  }, [bannerName, selectedCard]);

  const qrCodePopupProps = useMemo(
    () => ({
      open: openQrPopup,
      handleOpen: setOpenQrPopup,
      titleToShow: qrTitle,
      listOfItems: qrListItems,
      handleQrPopupOpen,
    }),
    [openQrPopup, qrListItems, qrTitle],
  );

  const handleLeadIntentPopupOpen = useCallback(
    () => setOpenLeadIntentPopup(true),
    [],
  );
  const handleLeadIntentPopupClose = useCallback(
    () => setOpenLeadIntentPopup(false),
    [],
  );

  const handleRequestACallBack = () => {
    lmsLeadIdWithCallBackTimeC(selectedCard);
    handleLeadIntentPopupClose();
    setOpenLeadIntentConfirmationPopup(true);
  };

  const initiateSuperAppRedirection = (data: GeneralBannerType) => {
    setIsLeadPopupsOpen(true);
    setIsLoading(true);
    const goToSuperAppInfo = getSuperAppRedirectionData(
      csAppSlice,
      userDetails,
      data?.attributes?.functionalityCode || "",
    );

    dispatch({
      type: EVENT_HANDLER.GO_TO_SUPER_APP_EVENT,
      payload: goToSuperAppInfo,
    });
    return;
  };

  const handleContinueInABCDApp = () => {
    generateLmsLeadIdForAppsFlyerRedirection(selectedCard);
    const appsFlyerLink = isEmpty(selectedCard)
      ? ""
      : selectedCard.attributes.appsFlyer;
    const webQrPopup = isEmpty(selectedCard)
      ? false
      : selectedCard.attributes.webQrPopup;

    if (platformFlags.isSuperApp) {
      initiateSuperAppRedirection(selectedCard);
      return;
    }
    if (platformFlags.isWeb) {
      if (isMobileBrowser) {
        openWebDestination(appsFlyerLink);
      } else {
        if (webQrPopup) {
          setOpenQrPopup(true);
        } else {
          openWebDestination(appsFlyerLink);
        }
      }
      handleLeadIntentPopupClose();
      return;
    }
    if (platformFlags.isMobileApp) {
      handleLeadIntentPopupClose();
      openAppDestination(appsFlyerLink);
    }
  };

  const handleGoBack = () => {
    generateLmsLeadIdForAppsFlyerRedirection(selectedCard);
    handleLeadIntentPopupClose();
  };

  // Lead Intent Popup Props
  const leadIntentPopupProps = useMemo(
    () => ({
      open: openLeadIntentPopup,
      handleOpen: setOpenLeadIntentPopup,
      data: selectedCard,
      handleRequestACallBack: handleRequestACallBack,
      handleContinueInABCDApp: handleContinueInABCDApp,
      handleGoBack: handleGoBack,
    }),
    [openLeadIntentPopup, selectedCard],
  );

  // Lead Intent Confirmation Popup Props
  const leadIntentConfirmationPopupProps = useMemo(
    () => ({
      open: openLeadIntentConfirmationPopup,
      handleOpen: setOpenLeadIntentConfirmationPopup,
    }),
    [openLeadIntentConfirmationPopup],
  );

  useEffect(() => {
    if (
      !openQrPopup &&
      !openBuyJourneyPopup &&
      !openLeadIntentPopup &&
      !openLeadIntentConfirmationPopup
    ) {
      setIsLeadPopupsOpen(false);
    }
  }, [
    openQrPopup,
    openBuyJourneyPopup,
    openLeadIntentPopup,
    openLeadIntentConfirmationPopup,
  ]);

  const handleClick = (data: GeneralBannerType) => {
    if (isPseudoUserLoggedIn) {
      return;
    }
    setSelectedCard(data);
    const appsFlyerLink = data?.attributes?.appsFlyer || "";
    const { isWebLead, isAppLead } = getLeadCreationFlags(data);
    const tempObj = {
      redirectionUrl: appsFlyerLink,
      abcName: userDetails?.name || "",
      abcMobile: userDetails?.mobileNumber || "",
      offerAmount: hasBannerMetrics(data) ? data.amount || "" : "",
      roi: hasBannerMetrics(data) ? data.roi || "" : "",
      bannerType: data?.attributes?.bannerType || "",
      displayType: isTrackWhatMatter || isBuyNewProduct ? "Icons" : "Banner",
      bannerPosition: bannerName || "",
      bannerName: data?.attributes?.bannerName || "",
      bannerHeader:
        (
          data as
            | T_TopBannerData
            | T_CenterBannerData
            | T_FooterBannerData
            | T_NudgeBannerData
        )?.attributes?.title ||
        (data as T_NewOfferingData | T_MyTrackData)?.attributes?.displayName ||
        data?.attributes?.productName ||
        "",
      bannerCategory: getBannerCategory(data),
      leadCreationFlag: platformFlags.isWeb ? isWebLead : isAppLead,
      product: data?.attributes?.productCategory || "",
      clickedDate: getFormattedDateTime(),
    };

    // keep same async behavior (not awaited)
    saveOfferMartAndPromotionalDetail(tempObj);

    if (checkForNamePopup) {
      setIsLeadPopupsOpen(true);
      openBuyJourneyPopupWithCard();
      return;
    }
    handleLeadJourney(data);
  };

  const handleLeadJourney = async (
    data: GeneralBannerType,
    buyJourneyUserName?: string,
  ) => {
    try {
      // Keep same check/behavior (as in your snippet)
      const appsFlyerLink = data?.attributes?.appsFlyer || "";
      const { isEnableSso, isWebLead, isAppLead, isWebAllowed, isAppAllowed } =
        getLeadCreationFlags(data);
      const leadPopup = !!data?.attributes?.leadPopup;
      const webQrPopup = !!data?.attributes?.webQrPopup;
      const enablePermissionPopup = !!data?.attributes?.enablePermissionPopup;

      // --- SuperApp flow unchanged
      if (platformFlags.isSuperApp) {
        generateLmsLeadIdForAppsFlyerRedirection(data, buyJourneyUserName);

        if (leadPopup) {
          setIsLeadPopupsOpen(true);
          handleLeadIntentPopupOpen();
          return;
        }
        initiateSuperAppRedirection(data);
        return;
      }

      // --- WEB flow unchanged
      if (platformFlags.isWeb) {
        // 1st case and 2nd case
        if (isEmpty(appsFlyerLink) && isWebLead && !webQrPopup) {
          await generateLeadIdAndTokenForLeadRedirection(
            data,
            buyJourneyUserName,
          );
          return;
        }
        // 3rd case and 4th case
        if (
          !isEmpty(appsFlyerLink) &&
          !isEnableSso &&
          isWebAllowed &&
          leadPopup
        ) {
          setIsLeadPopupsOpen(true);
          handleLeadIntentPopupOpen();
          return;
        }

        // 5th case
        if (
          !isEmpty(appsFlyerLink) &&
          !isEnableSso &&
          isWebAllowed &&
          !leadPopup &&
          webQrPopup
        ) {
          generateLmsLeadIdForAppsFlyerRedirection(data, buyJourneyUserName);
          if (isMobileBrowser) {
            openWebDestination(appsFlyerLink);
          } else {
            setIsLeadPopupsOpen(true);
            handleQrPopupOpen();
          }
          return;
        }

        // 6th case
        if (
          !isEmpty(appsFlyerLink) &&
          !isEnableSso &&
          isWebAllowed &&
          !leadPopup &&
          !webQrPopup
        ) {
          generateLmsLeadIdForAppsFlyerRedirection(data, buyJourneyUserName);
          openWebDestination(appsFlyerLink);
          return;
        }

        // 7th case
        if (
          !isEmpty(appsFlyerLink) &&
          !isEnableSso &&
          !isWebAllowed &&
          !leadPopup &&
          !webQrPopup
        ) {
          openWebDestination(appsFlyerLink);
          return;
        }

        // Default Case
        if (!isEmpty(appsFlyerLink)) {
          generateLmsLeadIdForAppsFlyerRedirection(data, buyJourneyUserName);
          openWebDestination(appsFlyerLink);
          return;
        }
      }

      // --- MOBILE APP flow unchanged
      if (platformFlags.isMobileApp) {
        // 1st case
        if (
          isEmpty(appsFlyerLink) &&
          isEnableSso &&
          isAppLead &&
          enablePermissionPopup
        ) {
          // ANDROID: defer execution to event listener (keep as-is)
          if (csAppSlice.NativeDeviceInfo?.systemName === "Android") {
            pendingRedirectionRef.current = {
              data,
              buyJourneyUserName,
              isAppLead,
              appsFlyerLink,
            };
            sendMessageToNative({ action: "inAppPermission" });
            // Fallback if native never responds (e.g., permission already granted)
            setTimeout(() => {
              if (pendingRedirectionRef.current) {
                window.dispatchEvent(
                  new CustomEvent("inAppPermissionResponse", {
                    detail: { granted: true },
                  }),
                );
              }
            }, 1500);
            return;
          }

          // iOS: proceed after native callback (keep as-is)
          nativeCallBackMethodForAppPermission({ type: "inAppPermission" })
            .then(async () => {
              await proceedAppRedirect({
                data,
                buyJourneyUserName,
                isAppLead,
                appsFlyerLink,
              });
              syncNativePermissions(
                userDetails?.mobileNumber ?? "",
                userDetails?.CS_USER_ID ?? "",
              );
            })
            .catch((error: any) => {
              console.log("Response from native code:", error);
            });

          return;
        }

        //2nd case
        if (
          isEmpty(appsFlyerLink) &&
          isEnableSso &&
          isAppLead &&
          !enablePermissionPopup
        ) {
          await proceedAppRedirect({
            data,
            buyJourneyUserName,
            isAppLead,
            appsFlyerLink,
          });
          return;
        }

        // 3rd case and 4rd case
        if (
          !isEmpty(appsFlyerLink) &&
          !isEnableSso &&
          isAppAllowed &&
          leadPopup &&
          !enablePermissionPopup
        ) {
          setIsLeadPopupsOpen(true);
          handleLeadIntentPopupOpen();
          return;
        }

        // 5th case and 6th case
        if (
          !isEmpty(appsFlyerLink) &&
          !isEnableSso &&
          isAppAllowed &&
          !leadPopup &&
          !enablePermissionPopup
        ) {
          generateLmsLeadIdForAppsFlyerRedirection(data, buyJourneyUserName);
          openAppDestination(appsFlyerLink);
          return;
        }
        // 7th case
        if (
          !isEmpty(appsFlyerLink) &&
          !isEnableSso &&
          !isAppAllowed &&
          !leadPopup &&
          !enablePermissionPopup
        ) {
          openAppDestination(appsFlyerLink);
          return;
        }

        // Default Case
        if (!isEmpty(appsFlyerLink)) {
          generateLmsLeadIdForAppsFlyerRedirection(data, buyJourneyUserName);
          openAppDestination(appsFlyerLink);
          return;
        }
      }
    } catch (e) {
      console.log("Error", e);
    }
  };

  useEffect(() => {
    const allPermissionsMessageHandler = (_event: any) => {
      if (!pendingRedirectionRef.current) return;

      const { data, buyJourneyUserName, isAppLead, appsFlyerLink } =
        pendingRedirectionRef.current;

      pendingRedirectionRef.current = null;

      (async () => {
        await proceedAppRedirect({
          data,
          buyJourneyUserName,
          isAppLead,
          appsFlyerLink,
        });
        syncNativePermissions(
          userDetails?.mobileNumber ?? "",
          userDetails?.CS_USER_ID ?? "",
        );
      })();
    };

    window.addEventListener(
      "inAppPermissionResponse",
      allPermissionsMessageHandler,
    );
    return () => {
      window.removeEventListener(
        "inAppPermissionResponse",
        allPermissionsMessageHandler,
      );
    };
  }, [proceedAppRedirect]);

  const buyJourneyPopupProps = useMemo(
    () => ({
      open: openBuyJourneyPopup,
      handleOpen: setOpenBuyJourneyPopup,
      cardsForJourneyPopup: selectedCard,
      handleLeadJourney: handleLeadJourney,
    }),
    [openBuyJourneyPopup, selectedCard],
  );

  return {
    handleClick,
    isLeadPopupsOpen,

    // Buy Journey
    buyJourney: {
      props: buyJourneyPopupProps,
      isOpen: openBuyJourneyPopup,
    },

    // QR Code
    qrCode: {
      props: qrCodePopupProps,
      isOpen: openQrPopup,
    },

    //Lead Intent
    leadIntent: {
      props: leadIntentPopupProps,
      isOpen: openLeadIntentPopup,
    },

    //Lead Intent Confirmation
    leadIntentConfirmation: {
      props: leadIntentConfirmationPopupProps,
      isOpen: openLeadIntentConfirmationPopup,
    },

    // Loading
    isLoading,
  };
};

export default useLeadRedirectionHandler;
